import { useMemo, useState } from "react";
import { MutationError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Select } from "../../ui";
import { useT } from "../../i18n/index";
import type { SegmentDefinition, SegmentMatch, SegmentRule } from "../../api/marketing-contracts";
import {
  useCreateSegment,
  useSavedSegments,
  useSegmentPrimitives,
  useUpdateSegment,
} from "./use-marketing";

// #291 — CR-1b saved-segment AUTHORING. The BE has POST/PATCH /v1/crm/saved-segments
// (segment:write@all) and a primitives vocabulary endpoint, but nothing consumed the
// write side — a MANAGER/ADMIN could not author a segment without hand-calling the API,
// while two readers (the console cockpit + the campaign `saved:<id>` target) already
// existed. This is that missing surface: list + compose from the governed primitives.
// The compiler owns the allow-list; a bad param 400s with a precise path, surfaced by
// MutationError. NO commission term anywhere (§1.6). Send is unaffected (still OFF).

// A draft rule holds param values as strings (form inputs); coerced to numbers where
// numeric on submit, and the server validates the rest in full (a precise-path 400).
interface DraftRule {
  primitive: string;
  params: Record<string, string>;
}

/** Build a rule's params from the form strings, coercing by the SERVER-declared param
 *  TYPE (not by shape-guessing): a `number` param is sent as a number, an enum/string
 *  param verbatim — so an all-digit code/enum is never mis-coerced. Empty → omitted. */
function buildParams(
  raw: Record<string, string>,
  descriptors: { name: string; type: "number" | "enum" | "string" }[],
): Record<string, unknown> {
  const byName = new Map(descriptors.map((d) => [d.name, d.type]));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    const t = v.trim();
    if (t === "") continue;
    out[k] = byName.get(k) === "number" ? Number(t) : t;
  }
  return out;
}

export function SavedSegments() {
  const { t } = useT();
  const { me } = usePrincipal();
  const canAuthor = hasGrant(me, "segment:write", "all");
  const segments = useSavedSegments();
  const primitives = useSegmentPrimitives();
  const create = useCreateSegment();
  const update = useUpdateSegment();

  const [name, setName] = useState("");
  const [match, setMatch] = useState<SegmentMatch>("ALL");
  const [rules, setRules] = useState<DraftRule[]>([]);

  const primitiveList = primitives.data?.primitives ?? [];
  const paramsOf = useMemo(() => {
    const map = new Map(primitiveList.map((p) => [p.key, p.params]));
    return (key: string) => map.get(key) ?? [];
  }, [primitiveList]);

  const addRule = () => {
    const first = primitiveList[0]?.key;
    if (!first) return;
    setRules((r) => [...r, { primitive: first, params: {} }]);
  };
  const removeRule = (i: number) => setRules((r) => r.filter((_, idx) => idx !== i));
  const setRulePrimitive = (i: number, primitive: string) =>
    setRules((r) => r.map((rule, idx) => (idx === i ? { primitive, params: {} } : rule)));
  const setRuleParam = (i: number, param: string, value: string) =>
    setRules((r) =>
      r.map((rule, idx) =>
        idx === i ? { ...rule, params: { ...rule.params, [param]: value } } : rule,
      ),
    );

  const canSubmit = name.trim().length > 0 && rules.length > 0 && !create.isPending;

  const submit = () => {
    const definition: SegmentDefinition = {
      match,
      rules: rules.map<SegmentRule>((r) => ({
        primitive: r.primitive,
        params: buildParams(r.params, paramsOf(r.primitive)),
      })),
    };
    create.mutate(
      { name: name.trim(), definition },
      {
        onSuccess: () => {
          setName("");
          setMatch("ALL");
          setRules([]);
        },
      },
    );
  };

  return (
    <section className="surf-stack" data-testid="saved-segments">
      <Card>
        <div className="surf-cardhead">
          <div>
            <h2 className="surf-cardtitle">{t("seg.title")}</h2>
            <p className="surf-sub muted">{t("seg.sub")}</p>
          </div>
        </div>

        {segments.isLoading && <LoadingState label={t("seg.loading")} rows={2} />}
        {segments.error instanceof Error && (
          <ErrorState title={t("seg.loadError")} description={t("seg.loadErrorDesc")} />
        )}
        {segments.data && segments.data.length === 0 && (
          <EmptyState title={t("seg.empty")} description={t("seg.emptyDesc")} />
        )}
        {segments.data && segments.data.length > 0 && (
          <ul className="seg-list" data-testid="seg-list">
            {segments.data.map((s) => (
              <li key={s.id} className="seg-row" data-testid="seg-row">
                <div className="seg-row__main">
                  <span className="seg-row__name">{s.name}</span>
                  <span className="seg-row__meta muted">
                    {t(`seg.match.${s.definition.match}` as "seg.match.ALL")} ·{" "}
                    {t("seg.ruleCount").replace("{n}", String(s.definition.rules.length))}
                  </span>
                </div>
                <Badge variant={s.status === "ACTIVE" ? "success" : "standard"}>
                  {t(`seg.status.${s.status}` as "seg.status.ACTIVE")}
                </Badge>
                {canAuthor && s.status === "ACTIVE" && (
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={() => update.mutate({ id: s.id, status: "ARCHIVED" })}
                    disabled={update.isPending}
                    data-testid="seg-archive"
                  >
                    {t("seg.archive")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <MutationError error={update.error} fallback={t("seg.updateError")} />
      </Card>

      {canAuthor && (
        <Card>
          <div className="surf-cardhead">
            <div>
              <h2 className="surf-cardtitle">{t("seg.new")}</h2>
              <p className="surf-sub muted">{t("seg.newSub")}</p>
            </div>
          </div>

          <div className="seg-form">
            <Input
              label={t("seg.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("seg.namePlaceholder")}
              data-testid="seg-name"
            />

            <Select
              label={t("seg.matchLabel")}
              value={match}
              onChange={(e) => setMatch(e.target.value as SegmentMatch)}
              data-testid="seg-match"
            >
              <option value="ALL">{t("seg.match.ALL")}</option>
              <option value="ANY">{t("seg.match.ANY")}</option>
            </Select>

            <div className="seg-rules">
              <p className="seg-rules__head">{t("seg.rules")}</p>
              {primitives.isLoading && <LoadingState label={t("seg.loading")} rows={1} />}
              {rules.length === 0 && <p className="muted seg-rules__empty">{t("seg.noRules")}</p>}
              {rules.map((rule, i) => (
                <div key={i} className="seg-rule" data-testid="seg-rule">
                  <Select
                    label={t("seg.primitive")}
                    value={rule.primitive}
                    onChange={(e) => setRulePrimitive(i, e.target.value)}
                  >
                    {primitiveList.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.key}
                      </option>
                    ))}
                  </Select>
                  {paramsOf(rule.primitive).map((p) =>
                    p.type === "enum" ? (
                      <Select
                        key={p.name}
                        label={p.required ? `${p.name} *` : p.name}
                        value={rule.params[p.name] ?? ""}
                        onChange={(e) => setRuleParam(i, p.name, e.target.value)}
                      >
                        <option value="">—</option>
                        {p.options?.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Input
                        key={p.name}
                        label={p.required ? `${p.name} *` : p.name}
                        type={p.type === "number" ? "number" : "text"}
                        value={rule.params[p.name] ?? ""}
                        onChange={(e) => setRuleParam(i, p.name, e.target.value)}
                        placeholder={p.name}
                      />
                    ),
                  )}
                  <Button variant="ghost" size="sm" type="button" onClick={() => removeRule(i)}>
                    {t("seg.removeRule")}
                  </Button>
                </div>
              ))}
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={addRule}
                disabled={primitiveList.length === 0}
                data-testid="seg-add-rule"
              >
                {t("seg.addRule")}
              </Button>
            </div>

            <MutationError error={create.error} fallback={t("seg.createError")} />

            <div className="seg-actions">
              <Button type="button" onClick={submit} disabled={!canSubmit} data-testid="seg-submit">
                {t("seg.create")}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </section>
  );
}
